import type { ReactNode } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Drawer,
  Toolbar,
} from '@mui/material';
import { Link as RouterLink, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAdminAuth } from '../../stores/authStore';
import { Seo } from '../../components/seo/Seo';

interface AdminLayoutProps {
  children?: ReactNode;
}

const DRAWER_WIDTH = 220;

/**
 * Admin shell — sidebar nav + main content area.
 *
 * Auth gate: redirects to `/` unless `useAdminAuth().isAdmin` is true.
 * In Phase 1 the auth store reads a JWT from localStorage; Phase 2 will
 * wire it to a real login flow.
 */
export function AdminLayout(_props: AdminLayoutProps) {
  const { isAdmin, isAuthenticated } = useAdminAuth();
  const location = useLocation();

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Seo title="Admin" noindex />
      <Drawer
        variant="permanent"
        component="nav"
        aria-label="Admin sections"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            position: 'relative',
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 0, py: 2 }}>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Admin
          </Typography>
        </Toolbar>
        <List>
          <ListItemButton
            component={RouterLink}
            to="/admin"
            selected={location.pathname === '/admin'}
          >
            <ListItemText primary="Dashboard" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/users"
            selected={location.pathname.startsWith('/admin/users')}
          >
            <ListItemText primary="Users" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/advertisers"
            selected={location.pathname.startsWith('/admin/advertisers')}
          >
            <ListItemText primary="Advertisers" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/review-queue"
            selected={location.pathname.startsWith('/admin/review-queue')}
          >
            <ListItemText primary="Review queue" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/retailers"
            selected={location.pathname.startsWith('/admin/retailers')}
          >
            <ListItemText primary="Retailers" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/scrape-jobs"
            selected={location.pathname.startsWith('/admin/scrape-jobs')}
          >
            <ListItemText primary="Scrape jobs" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/scrape-health"
            selected={location.pathname.startsWith('/admin/scrape-health')}
          >
            <ListItemText primary="Scrape health" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/alert-performance"
            selected={location.pathname.startsWith('/admin/alert-performance')}
          >
            <ListItemText primary="Alert performance" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/audit-log"
            selected={location.pathname.startsWith('/admin/audit-log')}
          >
            <ListItemText primary="Audit log" />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/admin/affiliates"
            selected={location.pathname.startsWith('/admin/affiliates')}
          >
            <ListItemText primary="Affiliates" />
          </ListItemButton>
        </List>
      </Drawer>
      <Box component="section" sx={{ flex: 1, p: { xs: 2, md: 3 } }} aria-label="Admin content">
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminLayout;
